import React, { useEffect, useState } from "react";
import {
  LinearProgress,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Box,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  IconButton,
} from "@mui/material";
import http from "../../utils/http";
import { errorToastMessage } from "../../utils/toast";
import {
  NoDataContainer,
  pageSize,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../Common/styles/table";
import DataTableLayout from "../Common/UI/DataTableLayout";
import DataTableFooter from "../Common/UI/DataTableFooter";
import { dataTableListShell } from "../../utils/dataTableShell";
import { AxiosResponse } from "axios";
import StatusBadge from "../Common/UI/StatusTag";
import AddSponsorModal from "./AddSponsorModal";
import { MoreVert } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { DateTime } from "luxon";

type Props = {
  canEdit: boolean;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const statusMap: any = {
  active: { label: "Active", variant: "active" },
  inactive: { label: "Inactive", variant: "cancelled" },
};

const SponsorsList = ({ canEdit, showModal, setShowModal }: Props) => {
  const [loading, setLoading] = useState(true);
  const [toggleLoader, setToggleLoader] = useState(false);
  const [sponsors, setSponsors] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [page, setPage] = useState<number>(0);
  const [, setSearchParams] = useSearchParams();
  const [sponsorsCount, setSponsorsCount] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", page.toString());

    setSearchParams(params, {
      replace: true,
    });
  }, [setSearchParams, page]);

  useEffect(() => {
    const fetchSponsors = async () => {
      try {
        setLoading(true);
        let url = `/admin/registry/sponsors?page=${page + 1}&size=${pageSize}`;

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.data;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          sponsorName: item?.sponsorName,
          sponsorType: item?.sponsorType,
          addressStreet: item?.addressStreet,
          addressCity: item?.addressCity,
          addressState: item?.addressState,
          addressZip: item?.addressZip,
          addressCountry: item?.addressCountry,
          contactName: item?.contactName,
          contactEmail: item?.contactEmail,
          contactPhone: item?.contactPhone,
          websiteUrl: item?.websiteUrl,
          isActive: item?.isActive,
          date: item?.createdAt
            ? DateTime.fromISO(item?.createdAt).toFormat("MM/dd/yyyy")
            : "-",
        }));

        setSponsors(newData || []);
        setSponsorsCount(
          res.data?.data?.meta?.paginationOptions?.totalItems || 0
        );
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchSponsors();
  }, [toggleLoader, page]);

  const refreshPage = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const openModal = () => {
    setShowModal(true);
    setAnchorEl(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRow(null);
  };

  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  return (
    <Box
      sx={{
        ...dataTableListShell,
        p: 2,
        backgroundColor: "#fff",
        borderRadius: "10px",
      }}
    >
      <DataTableLayout
        footer={
          <DataTableFooter
            filteredCount={sponsorsCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            loading={loading}
          />
        }
      >
        <Table
          sx={TableBorderRadiusTopLeftRight}
          stickyHeader
          className="scrollable-table"
        >
        <TableHead>
          <TableRow>
            <StyledTableCell>Name</StyledTableCell>
            <StyledTableCell>Type</StyledTableCell>
            <StyledTableCell>Creation Date</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            {canEdit && <StyledTableCell sx={{ maxWidth: 50 }} />}
          </TableRow>
        </TableHead>
        {!loading && sponsors?.length > 0 && (
          <TableBody>
            {sponsors?.map((row: any) => (
              <TableRow key={row?.id}>
                <StyledTableCell>{row?.sponsorName || "-"}</StyledTableCell>
                <StyledTableCell>{row?.sponsorType || "-"}</StyledTableCell>
                <StyledTableCell>{row?.date || "-"}</StyledTableCell>
                <StyledTableCell>
                  <StatusBadge
                    label={
                      statusMap[row?.isActive ? "active" : "inactive"]?.label
                    }
                    variant={
                      statusMap[row?.isActive ? "active" : "inactive"]?.variant
                    }
                  />
                </StyledTableCell>
                {canEdit && (
                  <StyledTableCell align="right">
                    <IconButton onClick={(e) => handleMenuClick(e, row)}>
                      <MoreVert />
                    </IconButton>
                  </StyledTableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        )}
        </Table>
        {!loading && sponsors.length === 0 && (
          <NoDataContainer>
            <Typography variant="body1" color="gray">
              No Data
            </Typography>
          </NoDataContainer>
        )}
        {loading && (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        )}
      </DataTableLayout>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: "400px",
            width: "20ch",
          },
        }}
        TransitionProps={{
          exit: false,
        }}
      >
        <MenuItem onClick={openModal}>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
      </Menu>

      {showModal && (
        <AddSponsorModal
          showModal={showModal}
          closeModal={closeModal}
          data={selectedRow}
          refreshPage={refreshPage}
        />
      )}
    </Box>
  );
};

export default SponsorsList;
